import type {
	AppID,
	CustomizationFailureCode,
	CustomizationGetResult,
	CustomizationResult,
	CustomizationTarget,
	ReadableCustomizationTarget,
} from "@tiendanube/nube-sdk-internal-types";
import { reportDevtoolsLog } from "../worker/handler/telemetry/devtools.ts";
import { type ErasedHandler, getHandler } from "./handlers";
import { EVERY_ELEMENT_KEY } from "./keys";
import { optionRefusal } from "./option-kinds";
import { forget, recall, remember } from "./snapshot";
import { CustomizationValidationError } from "./validations";

export type CustomizationController = {
	/**
	 * Apply a `CustomizationInput` to what is on the page right now.
	 * `input` is typed `unknown` on purpose: it comes off the worker wire,
	 * so it is validated here rather than trusted.
	 */
	set(input: unknown, appId: AppID): CustomizationResult;
	/** Restore the pristine value of every element matching `targets`. */
	reset(targets: unknown, appId: AppID): CustomizationResult;
	/**
	 * Read the resolved values of one target, as the page shows it now.
	 * `input` is a `CustomizationGetWireInput` off the worker wire, so it is
	 * validated here rather than trusted.
	 */
	get(
		input: unknown,
		appId: AppID,
	): CustomizationGetResult<ReadableCustomizationTarget>;
};

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Why a target's options cannot be applied, or `null` when they can.
 *
 * Options come off the worker wire, so neither their names nor the type of
 * their values is guaranteed, and each problem is worth its own words:
 *
 * - an **unknown** option name or a **mistyped** value, see `optionRefusal`;
 * - an **empty** object is what the types already forbid (a target's options
 *   are "at least one"), and it must not reach `operation`: with nothing to
 *   apply it walks past `validate`, takes a pristine snapshot and counts as
 *   applied, so the app is told `{ ok: true, applied: 1 }` about a call that
 *   changed nothing.
 */
function optionsRefusal(options: Record<string, unknown>): string | null {
	if (Object.keys(options).length === 0) return "no option was sent";
	for (const [name, value] of Object.entries(options)) {
		const refusal = optionRefusal(name, value);
		if (refusal) return refusal;
	}
	return null;
}

/**
 * The element whose values `get` reports: the first one a shopper can see.
 * `.js-addtocart` also matches the hidden placeholder Core themes keep beside
 * the real button, and its styles are not the ones on screen. Falls back to the
 * first match when none is rendered, rather than reporting nothing.
 */
function firstVisible(elements: Iterable<Element>): Element | undefined {
	let first: Element | undefined;
	for (const element of elements) {
		first ??= element;
		if (element.getClientRects().length > 0) return element;
	}
	return first;
}

/** A `console.warn` for the page, mirrored to the app's devtools logs. */
function warn(message: string, appId: AppID): void {
	console.warn(`[nube-sdk] ${message}`);
	reportDevtoolsLog(appId, "WARN", "customization", message);
}

/** A failure, before it is merged into the call's single result. */
type Failure = { code: CustomizationFailureCode; reason: string };

/** What happened to one target: how many elements took it, and why not. */
type TargetOutcome = {
	applied: number;
	refused: number;
	failure?: Failure;
};

/**
 * Report a failure the way this API reports everything: a `console.warn` for
 * whoever is looking at the page, and the code plus reason handed back so they
 * reach the app in the `CustomizationResult`.
 */
function refuse(
	code: CustomizationFailureCode,
	reason: string,
	appId: AppID,
): Failure {
	warn(`Customization: app "${appId}": ${reason}.`, appId);
	return { code, reason };
}

/**
 * Merge what happened to each target into the one result the app gets.
 *
 * With several failing targets the reason lists them all but the code can only
 * be one, so the first failure wins. That is a real limitation of a flat
 * result and the reason a per-target breakdown is the planned shape for bulk
 * calls — see ./README.md.
 */
function toResult(
	failures: Failure[],
	applied: number,
	refused: number,
): CustomizationResult {
	const [first] = failures;
	if (!first) return { ok: true, applied, refused };
	return {
		ok: false,
		code: first.code,
		reason: failures.map((failure) => failure.reason).join("; "),
	};
}

/**
 * Applies and undoes customizations on the host's DOM. One-shot by design:
 * each call touches the elements that exist at that moment and nothing here
 * watches the DOM afterwards.
 *
 * @see ./README.md
 */
export function createCustomizationController(): CustomizationController {
	/** A target nothing could be done to, with the reason already warned. */
	function failed(
		code: CustomizationFailureCode,
		reason: string,
		appId: AppID,
	): TargetOutcome {
		return { applied: 0, refused: 0, failure: refuse(code, reason, appId) };
	}

	/**
	 * Apply `options` to one element, or return why a rule refused them
	 * (already warned). Validation runs before `remember` on purpose, so a
	 * refused customization leaves no snapshot behind; and per element,
	 * because a rule may depend on where the element sits.
	 */
	function applyElement(
		handler: ErasedHandler,
		target: string,
		element: Element,
		options: object,
		appId: AppID,
	): string | null {
		const refusal = handler.validate?.(element, options) ?? null;
		if (refusal) {
			warn(
				new CustomizationValidationError(
					target as CustomizationTarget,
					appId,
					refusal,
				).message,
				appId,
			);
			return refusal;
		}

		// Pristine value first, and only the first time: it is what `reset`
		// restores, so it must survive repeated `set`s.
		remember(element, target as CustomizationTarget, handler.read(element));
		handler.operation(element, options);
		return null;
	}

	/**
	 * Apply what the worker resolved from an app's resolver: one rule per
	 * context, each finding its elements through `identify`.
	 *
	 * The outcome follows the rules of static options, element by element:
	 * one applied element makes the target ok, every matched element refused
	 * is `refused`, and no element matched is `not_found`. The resolver-only
	 * cases map onto existing codes: a resolver that threw (or options that
	 * are malformed) is the app's bug, so `invalid_input` once nothing was
	 * applied; and no rules at all means the app had nothing to change, a
	 * no-op like `reset` on a target that was never customized.
	 */
	function applyRules(
		handler: ErasedHandler,
		target: string,
		rules: unknown[],
		appId: AppID,
	): TargetOutcome {
		// Last wins: a product listed twice in the state resolves twice.
		const byKey = new Map<string, object>();
		const errors = new Set<string>();
		// Option names and types refused in any rule, which fail the target.
		const refusedOptions = new Set<string>();

		for (const rule of rules) {
			if (!isRecord(rule) || typeof rule.key !== "string") {
				errors.add("a rule is malformed");
				continue;
			}
			if (typeof rule.error === "string") {
				errors.add(`resolver failed for "${rule.key}": ${rule.error}`);
				continue;
			}
			if (!isRecord(rule.options)) {
				errors.add(
					`invalid options for "${rule.key}": options must be an object`,
				);
				continue;
			}
			const problem = optionsRefusal(rule.options);
			if (problem) {
				refusedOptions.add(`"${rule.key}": ${problem}`);
				continue;
			}
			byKey.set(rule.key, rule.options as object);
		}

		// An unknown option or a mistyped value refuses the whole target
		// before any rule applies, as it does for static options: otherwise a
		// valid rule next to it (the `"*"` one, say) would apply and answer
		// `ok: true`, and the option would not be feature-detectable. A
		// resolver that threw is different — the app's own code failing for
		// one context — and keeps the partial path below.
		if (refusedOptions.size > 0) {
			return failed(
				"invalid_input",
				`invalid options for target "${target}": ${[...refusedOptions].join(" / ")}`,
				appId,
			);
		}

		for (const error of errors) {
			warn(
				`Customization: app "${appId}": target "${target}": ${error}.`,
				appId,
			);
		}

		if (byKey.size === 0 && errors.size === 0)
			return { applied: 0, refused: 0 };

		let matched = 0;
		let applied = 0;
		let refusedElements = 0;
		const refusals = new Set<string>();

		for (const element of document.querySelectorAll(handler.selector)) {
			// A per-element rule first, then the one resolved without context,
			// which applies to every element.
			const key = handler.identify?.(element) ?? null;
			const options =
				(key === null ? undefined : byKey.get(key)) ??
				byKey.get(EVERY_ELEMENT_KEY);
			if (!options) continue;

			matched++;
			const refusal = applyElement(handler, target, element, options, appId);
			if (refusal) {
				refusedElements++;
				refusals.add(refusal);
			} else {
				applied++;
			}
		}

		if (applied > 0) return { applied, refused: refusedElements };

		// Already warned individually, so none of these is re-reported.
		if (errors.size > 0) {
			return {
				applied: 0,
				refused: refusedElements,
				failure: {
					code: "invalid_input",
					reason: `invalid options for target "${target}": ${[...errors].join(" / ")}`,
				},
			};
		}
		if (matched === 0) {
			return failed(
				"not_found",
				`target "${target}" matched no element on this page; call "set" again once it exists`,
				appId,
			);
		}
		return {
			applied: 0,
			refused: refusedElements,
			failure: {
				code: "refused",
				reason: `target "${target}" was refused on every matching element: ${[
					...refusals,
				].join(" / ")}`,
			},
		};
	}

	function applyTarget(
		target: string,
		options: unknown,
		appId: AppID,
	): TargetOutcome {
		const handler = getHandler(target);
		if (!handler) {
			return failed("unknown_target", `unknown target "${target}"`, appId);
		}

		if (Array.isArray(options)) {
			return applyRules(handler, target, options, appId);
		}

		if (!isRecord(options)) {
			return failed(
				"invalid_input",
				`invalid options for target "${target}": options must be an object`,
				appId,
			);
		}

		const optionsProblem = optionsRefusal(options);
		if (optionsProblem) {
			return failed(
				"invalid_input",
				`invalid options for target "${target}": ${optionsProblem}`,
				appId,
			);
		}

		const elements = document.querySelectorAll(handler.selector);
		if (elements.length === 0) {
			return failed(
				"not_found",
				`target "${target}" matched no element on this page; call "set" again once it exists`,
				appId,
			);
		}

		let applied = 0;
		let refusedElements = 0;
		// Distinct reasons, so N elements refused for the same reason report it
		// once.
		const refusals = new Set<string>();

		for (const element of elements) {
			const refusal = applyElement(handler, target, element, options, appId);
			if (refusal) {
				refusedElements++;
				refusals.add(refusal);
			} else {
				applied++;
			}
		}

		// One applied element is a customization a shopper can see, so the
		// call succeeds even when a twin was refused — `.js-addtocart` also
		// matches the theme's hidden placeholder, and failing the whole call
		// over an invisible element would train apps to ignore the result.
		// The counts still report it, and each refusal was warned about
		// individually above.
		if (applied > 0) return { applied, refused: refusedElements };

		return {
			applied: 0,
			refused: elements.length,
			// Already warned per element, so this one is not re-reported.
			failure: {
				code: "refused",
				reason: `target "${target}" was refused on every matching element: ${[
					...refusals,
				].join(" / ")}`,
			},
		};
	}

	return {
		set(input, appId) {
			if (!isRecord(input)) {
				return {
					ok: false,
					...refuse(
						"invalid_input",
						'"set" was called without a valid target object',
						appId,
					),
				};
			}

			const entries = Object.entries(input);
			if (entries.length === 0) {
				// Same lie as an empty options object: nothing was asked for, so
				// reporting success would be reporting a change that never
				// happened. `reset([])` is deliberately NOT symmetrical — an
				// empty list is a typed, legitimate "nothing to restore", which
				// an app can arrive at by computing the list.
				return {
					ok: false,
					...refuse(
						"invalid_input",
						'"set" was called without a target',
						appId,
					),
				};
			}

			const failures: Failure[] = [];
			let applied = 0;
			let refused = 0;

			for (const [target, options] of entries) {
				const outcome = applyTarget(target, options, appId);
				applied += outcome.applied;
				refused += outcome.refused;
				if (outcome.failure) failures.push(outcome.failure);
			}

			return toResult(failures, applied, refused);
		},

		reset(targets, appId) {
			if (!Array.isArray(targets)) {
				return {
					ok: false,
					...refuse(
						"invalid_input",
						'"reset" was called without a list of targets',
						appId,
					),
				};
			}

			const failures: Failure[] = [];
			let restored = 0;

			for (const target of targets) {
				if (typeof target !== "string") {
					failures.push(
						refuse(
							"invalid_input",
							'"reset" was called with a non-string target',
							appId,
						),
					);
					continue;
				}

				const handler = getHandler(target);
				if (!handler) {
					failures.push(
						refuse("unknown_target", `unknown target "${target}"`, appId),
					);
					continue;
				}

				// A target with nothing to restore is a no-op, not a failure:
				// the element may have been replaced, or never customized.
				for (const element of document.querySelectorAll(handler.selector)) {
					const pristine = recall(element, target as CustomizationTarget);
					if (!pristine) continue;
					handler.operation(element, pristine);
					forget(element, target as CustomizationTarget);
					restored++;
				}
			}

			// Nothing here validates, so an element is never refused on reset.
			return toResult(failures, restored, 0);
		},

		get(input, appId) {
			if (!isRecord(input) || typeof input.target !== "string") {
				return {
					ok: false,
					...refuse(
						"invalid_input",
						'"get" was called without a target',
						appId,
					),
				};
			}
			const { target, matches } = input;

			// Every target of the catalog is readable, so this is the same
			// feature detection as `set`'s: this SDK version has no such target.
			const handler = getHandler(target);
			if (!handler) {
				return {
					ok: false,
					...refuse(
						"unknown_target",
						`target "${target}" cannot be read`,
						appId,
					),
				};
			}

			if (matches !== undefined && !Array.isArray(matches)) {
				return {
					ok: false,
					...refuse(
						"invalid_input",
						`invalid predicate outcome for target "${target}"`,
						appId,
					),
				};
			}

			// Without a predicate every element is a candidate. With one, only
			// those whose key it selected — or all of them, for a target
			// resolved without context.
			let isCandidate: (element: Element) => boolean = () => true;
			if (matches) {
				const keys = new Set<string>();
				const errors = new Set<string>();
				for (const match of matches) {
					if (!isRecord(match) || typeof match.key !== "string") {
						errors.add("a predicate outcome is malformed");
					} else if (typeof match.error === "string") {
						errors.add(`predicate failed for "${match.key}": ${match.error}`);
					} else {
						keys.add(match.key);
					}
				}
				for (const error of errors) {
					warn(
						`Customization: app "${appId}": target "${target}": ${error}.`,
						appId,
					);
				}

				if (keys.size === 0) {
					// Already warned individually, so not re-reported.
					if (errors.size > 0) {
						return {
							ok: false,
							code: "invalid_input",
							reason: `invalid predicate for target "${target}": ${[...errors].join(" / ")}`,
						};
					}
					return {
						ok: false,
						...refuse(
							"not_found",
							`the predicate for target "${target}" selected nothing on this page`,
							appId,
						),
					};
				}

				isCandidate = keys.has(EVERY_ELEMENT_KEY)
					? () => true
					: (element) => {
							const key = handler.identify?.(element) ?? null;
							return key !== null && keys.has(key);
						};
			}

			// Read on every call, never cached: the values change with a
			// variant selection, a breakpoint or a re-render, none of which
			// this module observes.
			const element = firstVisible(
				[...document.querySelectorAll(handler.selector)].filter(isCandidate),
			);
			if (!element) {
				return {
					ok: false,
					...refuse(
						"not_found",
						matches
							? `the predicate for target "${target}" selected elements this page does not have; call "get" again once they exist`
							: `target "${target}" matched no element on this page; call "get" again once it exists`,
						appId,
					),
				};
			}

			return {
				ok: true,
				values: handler.values(element),
			} as CustomizationGetResult<ReadableCustomizationTarget>;
		},
	};
}
